import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '../lib/gsapContext';

const scenes = [
  {
    step: '01',
    title: 'Blueprint',
    body: 'Architecture, credentials, build spec, test cases, edge cases, deployment plan. The Blueprint is the contract — fixed scope, fixed fee, before code.',
  },
  {
    step: '02',
    title: 'Build',
    body: 'Engineering-grade implementation against the Blueprint. Every test case from the Blueprint is enforced in CI. Nothing ships unverified.',
  },
  {
    step: '03',
    title: 'Deploy',
    body: 'Production deployment with monitoring, alerts, and a runbook. Telemetry from day one. You see what the system is doing and what it has done.',
  },
];

export default function ProtocolStack() {
  const root = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!root.current) return;
    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>('[data-protocol-card]');

      cards.forEach((card, i) => {
        gsap.from(card, {
          opacity: 0,
          y: 60,
          duration: 0.8,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 80%',
            toggleActions: 'play none none reverse',
          },
        });

        // Subsequent cards dim the previous ones slightly
        if (i > 0) {
          gsap.to(cards[i - 1], {
            opacity: 0.5,
            scale: 0.97,
            duration: 0.4,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: card,
              start: 'top 60%',
              toggleActions: 'play none none reverse',
            },
          });
        }
      });
    }, root);
    return () => {
      ctx.revert();
      ScrollTrigger.refresh();
    };
  }, []);

  return (
    <section ref={root} className="grain relative bg-black py-32">
      <div className="container-x mb-20">
        <p className="label mb-6 inline-flex items-center gap-3">
          <span className="h-px w-10 bg-gold" />
          The Protocol
        </p>
        <h2 className="max-w-3xl font-display text-section font-light text-white text-balance">
          Three phases. One unbroken delivery line.
        </h2>
      </div>

      <div className="container-x">
        <div className="flex flex-col gap-8">
          {scenes.map((s, i) => (
            <div
              key={s.step}
              data-protocol-card
              className="grain relative flex flex-col gap-8 overflow-hidden rounded-5xl border border-white/10 bg-charcoal p-10 md:flex-row md:items-center md:gap-16 will-change-transform"
            >
              <div className="flex-shrink-0 md:w-64">
                <span className="font-mono text-xs uppercase tracking-label text-gold">
                  Phase {s.step}
                </span>
                <h3 className="mt-4 font-display text-4xl font-light text-white">
                  {s.title}
                </h3>
              </div>

              <div className="flex flex-1 flex-col gap-8 md:flex-row md:items-center md:gap-12">
                <p className="max-w-xl flex-1 font-sans text-body text-white/85">
                  {s.body}
                </p>
                <div className="flex-shrink-0">
                  <SceneArt step={i} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SceneArt({ step }: { step: number }) {
  if (step === 0) {
    return (
      <svg viewBox="0 0 120 120" className="h-24 w-24 text-gold opacity-70">
        {[15, 25, 35, 45, 55].map((r) => (
          <circle
            key={r}
            cx="60"
            cy="60"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeOpacity={1 - r / 80}
            strokeWidth="0.8"
          >
            <animateTransform
              attributeName="transform"
              type="rotate"
              from={`0 60 60`}
              to={`${r % 2 === 0 ? 360 : -360} 60 60`}
              dur={`${6 + r / 10}s`}
              repeatCount="indefinite"
            />
          </circle>
        ))}
        <circle cx="60" cy="60" r="3" fill="currentColor" />
      </svg>
    );
  }
  if (step === 1) {
    return (
      <div className="relative h-24 w-48 overflow-hidden rounded-2xl border border-white/10 bg-black/60 p-3">
        <div className="grid grid-cols-8 gap-1">
          {Array.from({ length: 32 }).map((_, i) => (
            <span
              key={i}
              className="block h-2 w-2 rounded-full bg-white/20"
            />
          ))}
        </div>
        <div
          className="absolute inset-y-0 w-0.5 bg-gold opacity-80"
          style={{
            animation: 'scanline 2.4s ease-in-out infinite',
          }}
        />
        <style>{`
          @keyframes scanline {
            0%   { left: 12px; opacity: 0; }
            10%  { opacity: 0.8; }
            90%  { opacity: 0.8; }
            100% { left: calc(100% - 14px); opacity: 0; }
          }
        `}</style>
      </div>
    );
  }
  return (
    <svg viewBox="0 0 200 60" className="h-16 w-48 text-gold opacity-80">
      <path
        d="M0 30 L30 30 L40 15 L55 45 L70 22 L85 38 L100 30 L200 30"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="320"
        strokeDashoffset="320"
      >
        <animate
          attributeName="stroke-dashoffset"
          from="320"
          to="0"
          dur="2s"
          repeatCount="indefinite"
          begin="0s"
        />
      </path>
      <circle cx="100" cy="30" r="2.5" fill="currentColor">
        <animate
          attributeName="opacity"
          values="0;1;0"
          dur="2s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}
